/**
 * UI Store
 * Zustand store for global UI state management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Drawer state
 */
export interface DrawerState {
  isOpen: boolean;
  title: string;
  content: React.ReactNode | null;
  width?: number | string;
}

/**
 * Modal state
 */
export interface ModalState {
  isOpen: boolean;
  title: string;
  content: React.ReactNode | null;
  width?: number;
  footer?: React.ReactNode | null;
}

/**
 * Notification state
 */
export interface NotificationState {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  description?: string;
  duration?: number;
}

/**
 * UI store state
 */
export interface UIState {
  // Global loading state
  isGlobalLoading: boolean;
  loadingMessage: string | null;

  // Sidebar state
  isSidebarCollapsed: boolean;

  // Drawer state
  drawer: DrawerState;

  // Modal state
  modal: ModalState;

  // Theme
  isDarkMode: boolean;

  // Actions - Global loading
  setGlobalLoading: (isLoading: boolean, message?: string) => void;

  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Actions - Drawer
  openDrawer: (config: Omit<DrawerState, 'isOpen'>) => void;
  closeDrawer: () => void;

  // Actions - Modal
  openModal: (config: Omit<ModalState, 'isOpen'>) => void;
  closeModal: () => void;

  // Actions - Theme
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  isGlobalLoading: false,
  loadingMessage: null,
  isSidebarCollapsed: false,
  drawer: {
    isOpen: false,
    title: '',
    content: null,
    width: 600,
  },
  modal: {
    isOpen: false,
    title: '',
    content: null,
    width: 520,
    footer: null,
  },
  isDarkMode: false,
};

/**
 * UI Zustand store
 */
export const useUIStore = create<UIState>()(
  devtools(
    (set) => ({
      ...initialState,

      // Global loading actions
      setGlobalLoading: (isLoading, message) =>
        set(
          {
            isGlobalLoading: isLoading,
            loadingMessage: isLoading ? message || null : null,
          },
          false,
          'ui/setGlobalLoading'
        ),

      // Sidebar actions
      toggleSidebar: () =>
        set(
          (state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }),
          false,
          'ui/toggleSidebar'
        ),

      setSidebarCollapsed: (collapsed) =>
        set({ isSidebarCollapsed: collapsed }, false, 'ui/setSidebarCollapsed'),

      // Drawer actions
      openDrawer: (config) =>
        set(
          {
            drawer: {
              ...config,
              isOpen: true,
            },
          },
          false,
          'ui/openDrawer'
        ),

      closeDrawer: () =>
        set(
          {
            drawer: {
              isOpen: false,
              title: '',
              content: null,
              width: 600,
            },
          },
          false,
          'ui/closeDrawer'
        ),

      // Modal actions
      openModal: (config) =>
        set(
          {
            modal: {
              ...config,
              isOpen: true,
            },
          },
          false,
          'ui/openModal'
        ),

      closeModal: () =>
        set(
          {
            modal: {
              isOpen: false,
              title: '',
              content: null,
              width: 520,
              footer: null,
            },
          },
          false,
          'ui/closeModal'
        ),

      // Theme actions
      toggleDarkMode: () =>
        set(
          (state) => ({ isDarkMode: !state.isDarkMode }),
          false,
          'ui/toggleDarkMode'
        ),

      setDarkMode: (isDark) =>
        set({ isDarkMode: isDark }, false, 'ui/setDarkMode'),

      // Reset action
      reset: () =>
        set(initialState, false, 'ui/reset'),
    }),
    { name: 'UIStore' }
  )
);

/**
 * Selectors for common queries
 */

// Check if any modal or drawer is open
export const useHasOpenOverlay = () => {
  return useUIStore((state) => state.drawer.isOpen || state.modal.isOpen);
};

// Get current theme
export const useTheme = () => {
  return useUIStore((state) => (state.isDarkMode ? 'dark' : 'light'));
};
